import React, { useState, useMemo } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Button,
  TextField,
  Typography,
  CircularProgress,
  Slider,
  Switch,
  Dialog,
  DialogActions,
  DialogContent,
  Autocomplete,
} from '@mui/material'
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import SearchIcon from '@mui/icons-material/Search'
import DeliveryDateTimePicker from '../../components/Modals/DeliveryDateTimePicker'
import dayjs from 'dayjs'
import { DndProvider } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import {
  NavBar,
  OccasionalDriverModal,
  ConfirmationModal,
} from '../../components'
import DriverCardFullScreen from '../../components/Planning/DriverCardFullScreen'
import OrderCardFullScreen from '../../components/Planning/OrderCardFullScreen'
import OdlDropZone from '../../components/Planning/OdlDropZone'
import { CustomDialogTitle } from '../../components/Modals/CustomerStyledComponents'
import { send } from '../../lib/bus'
import '../../components/Modals/ModalStyle.scss'

function PlanningPageFullScreen({
  selectedDate,
  setSelectedDate,
  drivers,
  isPlanificationSentAtLeastOnce,
  isPastDate,
  isTodayOrNextBusinessDay,
  loading,
  orders,
  isModalOpen,
  handleModalClose,
  handleRefreshPlanning,
  handleAddOccasionalDriver,
  handleSendPlanification,
  handleConfirmSend,
  setIsConfirmationOpen,
  isConfirmationOpen,
  moveOrder,
  moveOrderToList,
  toggleDriverStatus,
  toggleFullScreen,
  onExportExcel,
}) {
  const { t } = useTranslation('planning_page')
  const [statusFilter] = useState(null)
  const [selectedOrderIds, setSelectedOrderIds] = useState(() => new Set())
  const [searchTerm, setSearchTerm] = useState('')
  const [timeFilterType, setTimeFilterType] = useState('exact')
  const [filterDate, setFilterDate] = useState(selectedDate.format('YYYY-MM-DD'))
  const [filterTimeStart, setFilterTimeStart] = useState('')
  const [filterTimeEnd, setFilterTimeEnd] = useState('')
  const [planningSearchTerm, setPlanningSearchTerm] = useState('')
  const [driverFilterType, setDriverFilterType] = useState('all')
  const [odlCountFilter, setOdlCountFilter] = useState(-1)
  const [isOdlFullPage, setIsOdlFullPage] = useState(false)
  const [isPlanningFullPage, setIsPlanningFullPage] = useState(false)
  const [isAttributionOpen, setIsAttributionOpen] = useState(false)
  const [attributionDriverId, setAttributionDriverId] = useState('')
  const [attributionError, setAttributionError] = useState('')

  const resolveSite = (node) => {
    const parts = []
    if (node.startingSiteBdn?.address) parts.push(node.startingSiteBdn.address)
    else if (node.startingSite?.address) parts.push([node.startingSite.address, node.startingSite.city].filter(Boolean).join(' '))
    else if (node.startingSiteAddress) parts.push([node.startingSiteAddress, node.startingSiteCity].filter(Boolean).join(' '))
    if (node.deliverySiteBdn?.address) parts.push(node.deliverySiteBdn.address)
    else if (node.deliverySite?.address) parts.push([node.deliverySite.address, node.deliverySite.city].filter(Boolean).join(' '))
    else if (node.deliverySiteAddress) parts.push([node.deliverySiteAddress, node.deliverySiteCity].filter(Boolean).join(' '))
    if (node.unloadingSiteBdn?.address) parts.push(node.unloadingSiteBdn.address)
    else if (node.unloadingSite?.address) parts.push([node.unloadingSite.address, node.unloadingSite.city].filter(Boolean).join(' '))
    else if (node.unloadingSiteAddress) parts.push([node.unloadingSiteAddress, node.unloadingSiteCity].filter(Boolean).join(' '))
    return parts.join(' ').toLowerCase()
  }

  const handleToggleSelect = (id) => {
    setSelectedOrderIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const handleAttribuer = async () => {
    setAttributionError('')
    try {
      const selectedEdges = orders?.orderDeliveries?.edges.filter(
        (o) => selectedOrderIds.has(o.node.id)
      ) || []
      if (selectedEdges.length === 0) return
      for (const edge of selectedEdges) {
        await moveOrder({ id: edge.node.id, name: edge.node.name, fromDriverId: null }, attributionDriverId, null)
      }
      setSelectedOrderIds(new Set())
      setIsAttributionOpen(false)
      setAttributionDriverId('')
    } catch (error) {
      setAttributionError(error.message || 'Erreur lors de l\'attribution')
    }
  }

  const unassignedOrders = (orders?.orderDeliveries?.edges ?? []).filter(
    (o) => o.node.userId === null
  )
  const filteredOrders = useMemo(() => {
    let list = statusFilter
      ? unassignedOrders.filter((o) => o.node.status === statusFilter)
      : unassignedOrders
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase().trim()
      const termNoSpaces = term.replace(/\s/g, '')
      list = list.filter((o) => {
        const node = o.node
        const cmd = node.command || {}
        if ((cmd.commandNumber || '').toLowerCase().includes(term)) return true
        if ((cmd.clientName || '').toLowerCase().includes(term)) return true
        if ((node.orderDeliveryNumber || '').toLowerCase().includes(term)) return true
        const contactPhone = (cmd.contactPhone || '').replace(/\s/g, '')
        if (contactPhone.includes(termNoSpaces)) return true
        const phoneNumber = (node.phoneNumber || '').replace(/\s/g, '')
        if (phoneNumber.includes(termNoSpaces)) return true
        if (resolveSite(node).includes(term)) return true
        const materials = node.orderDeliveryMaterials?.nodes || []
        for (const m of materials) {
          if ((m.material?.description || '').toLowerCase().includes(term)) return true
          if ((m.material?.internalId || '').toLowerCase().includes(term)) return true
        }
        return false
      })
    }
    if (timeFilterType === 'exact' && filterDate && filterTimeStart) {
      list = list.filter((o) => {
        const node = o.node
        return node.deliveryDate === filterDate && node.deliveryTimeStart && node.deliveryTimeStart.startsWith(filterTimeStart)
      })
    }
    if (timeFilterType === 'range' && filterDate && filterTimeStart && filterTimeEnd) {
      list = list.filter((o) => {
        const node = o.node
        return node.deliveryDate === filterDate && node.deliveryTimeStart && node.deliveryTimeEnd &&
          node.deliveryTimeStart <= filterTimeEnd && node.deliveryTimeEnd >= filterTimeStart
      })
    }
    return list
  }, [unassignedOrders, statusFilter, searchTerm, timeFilterType, filterDate, filterTimeStart, filterTimeEnd])

  const filteredDrivers = useMemo(() => {
    let list = drivers || []
    if (driverFilterType === 'active') {
      list = list.filter((d) => !d.isOutOfOfficeToday)
    }
    if (odlCountFilter >= 0) {
      list = list.filter((d) => {
        const count = d.orderDeliveries?.edges?.length ?? 0
        return odlCountFilter === 6 ? count >= 6 : count === odlCountFilter
      })
    }
    if (planningSearchTerm.trim()) {
      const term = planningSearchTerm.toLowerCase().trim()
      const termNoSpaces = term.replace(/\s/g, '')
      list = list
        .map((d) => {
          const driverName = `${d.firstname} ${d.lastname}`.toLowerCase()
          if (driverName.includes(term)) return d
          const matchingEdges = (d.orderDeliveries?.edges ?? []).filter((edge) => {
            const node = edge.node
            const cmd = node.command || {}
            if ((cmd.commandNumber || '').toLowerCase().includes(term)) return true
            if ((cmd.clientName || '').toLowerCase().includes(term)) return true
            if ((node.orderDeliveryNumber || '').toLowerCase().includes(term)) return true
            const contactPhone = (cmd.contactPhone || '').replace(/\s/g, '')
            if (contactPhone.includes(termNoSpaces)) return true
            const phoneNumber = (node.phoneNumber || '').replace(/\s/g, '')
            if (phoneNumber.includes(termNoSpaces)) return true
            if (resolveSite(node).includes(term)) return true
            const deliveryCity = node.deliverySite?.city || node.deliverySiteBdn?.city || node.deliverySiteCity || ''
            if (deliveryCity.toLowerCase().includes(term)) return true
            const materials = node.orderDeliveryMaterials?.nodes || []
            for (const m of materials) {
              if ((m.material?.description || '').toLowerCase().includes(term)) return true
              if ((m.material?.internalId || '').toLowerCase().includes(term)) return true
            }
            return false
          })
          if (matchingEdges.length === 0) return null
          return { ...d, orderDeliveries: { ...d.orderDeliveries, edges: matchingEdges } }
        })
        .filter(Boolean)
    }
    return list
  }, [drivers, planningSearchTerm, driverFilterType, odlCountFilter])

  const odlFilter = (
    <Box sx={{ display: 'flex', flexDirection: 'column', p: 2, gap: '10px' }}>
      <TextField
        size="small"
        placeholder={t('searchOrders')}
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        InputProps={{ startAdornment: <SearchIcon sx={{ stroke: '#737373', fontSize: 20, mr: 1.5, position: 'static' }} /> }}
      />

      <DeliveryDateTimePicker
        deliveryDate={filterDate}
        deliveryTimeStart={filterTimeStart}
        deliveryTimeEnd={filterTimeEnd}
        timeFilterType={timeFilterType}
        onDeliveryDateChange={(value) => {
          setFilterDate(value)
          setSelectedDate(dayjs(value))
          send('date:changed', { date: value })
        }}
        onDeliveryTimeStartChange={(value) => setFilterTimeStart(value)}
        onDeliveryTimeEndChange={(value) => setFilterTimeEnd(value)}
        onTimeFilterTypeChange={(type) => setTimeFilterType(type)}
        isNotModifiable={false}
        hideTitle
        disableMargin
        sx={{ mb: 0 }}
      />
    </Box>
  )

  return (
    <DndProvider backend={HTML5Backend}>
      <Box
        sx={{
          display: 'flex',
          width: '100vw',
          height: '100vh',
          overflow: 'hidden',
          backgroundColor: '#F4F4F4',
        }}
        className="fullscreenodl"
      >
        {/* Zone 1 — MenuLateral (71px) */}
        <Box
          sx={{
            width: 71,
            height: '100vh',
            flexShrink: 0,
            backgroundColor: 'white',
            overflow: 'hidden',
          }}
        >
          <Box onClick={toggleFullScreen}><NavBar active="planning" minimize={true} /></Box>
        </Box>

        {/* Zone 2 — ODL (468px fixe, ou plein écran si isOdlFullPage, caché si isPlanningFullPage) */}
        {!isPlanningFullPage && (
        <Box
          sx={{
            width: isOdlFullPage ? undefined : 468,
            flex: isOdlFullPage ? 1 : undefined,
            flexShrink: isOdlFullPage ? undefined : 0,
            height: '100vh',
            display: 'flex',
            padding: '24px',
            flexDirection: 'column',
            backgroundColor: '#F4F4F4',
            borderRight: '1px solid #E0E0E0',
          }}
        >
          {/* Header ODL — titre + toggle plein écran */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: "20px", mx: "2px" }}>
            <Typography variant="h5">
              {t('orderDeliveries')}
            </Typography>
            <Box display="flex" gap={1}>
              {isOdlFullPage && (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={toggleFullScreen}
                  sx={{ minWidth: 32, width: 32, height: 32, p: 0, backgroundColor: 'white', borderColor: '#737373' }}
                >
                  <CloseFullscreenIcon sx={{ position: 'static', stroke: '#737373' }} />
                </Button>
              )}
              <Button
                variant="outlined"
                size="small"
                onClick={() => setIsOdlFullPage((v) => !v)}
                sx={{ minWidth: 32, width: 32, height: 32, p: 0, backgroundColor: 'white', borderColor: '#737373' }}
              >
                {isOdlFullPage ? (
                  <ChevronLeftIcon sx={{ position: 'static', color: '#737373' }} />
                ) : (
                  <ChevronRightIcon sx={{ position: 'static', color: '#737373' }} />
                )}
              </Button>
            </Box>
          </Box>

          {/* ODL — filtre fixe puis cartes scrollables */}
          {!isOdlFullPage && (
            <Box sx={{ display: 'flex', flexDirection: 'column', backgroundColor: 'white', borderRadius: 1, boxShadow: 1, mb: 1 }}>
              {odlFilter}
            </Box>
          )}
          <Box sx={{ flex: 1, overflowY: 'auto' }}>
            <OdlDropZone
              orders={filteredOrders}
              moveOrderToList={moveOrderToList}
              isPastDate={isPastDate}
              selectedOrderIds={selectedOrderIds}
              onToggleSelect={handleToggleSelect}
              isOdlFullPage={isOdlFullPage}
            >
              {isOdlFullPage && odlFilter}
            </OdlDropZone>
          </Box>

          {/* Footer ODL — bouton Attribuer */}
          <Box sx={{ flexShrink: 0, display: 'flex', justifyContent: 'flex-end', paddingTop: "10px" }}>
            <Button variant="contained" color="primary" disabled={selectedOrderIds.size === 0}
              sx={{ height: 41 }}
              onClick={() => { setIsAttributionOpen(true); setAttributionError(''); }}
            >
              Attribuer
            </Button>
          </Box>
          </Box>
        )}

        {/* Zone 3 — Planning */}
        {!isOdlFullPage && (
        <Box
          sx={{
            flex: 1,
            height: '100vh',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            background: '#FFF',
            padding: '24px',
          }}
        >
          {/* Header Planning — titre et actions */}
          <Box>
            <Box display="flex" alignItems="flex-start" justifyContent="space-between">
              <Box display="flex" alignItems="center" gap={1}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setIsPlanningFullPage((v) => !v)}
                  sx={{ minWidth: 32, width: 32, height: 32, p: 0, backgroundColor: 'white', borderColor: '#737373' }}
                >
                  {isPlanningFullPage ? (
                    <ChevronRightIcon sx={{ position: 'static', color: '#737373' }} />
                  ) : (
                    <ChevronLeftIcon sx={{ position: 'static', color: '#737373' }} />
                  )}
                </Button>
                <Typography variant="h5">
                  {t('planningDrivers')}
                </Typography>
                <Typography variant="h6" sx={{ color: '#666', fontWeight: 400 }}>
                  — {selectedDate.format('dddd D MMMM YYYY').replace(/^\w/, c => c.toUpperCase())}
                </Typography>
              </Box>
              <Box display="flex" gap={1}>
              <Button
                  variant="outlined"
                  size="small"
                  onClick={toggleFullScreen}
                  sx={{ minWidth: 40, width: 40, height: 40, p: 0, backgroundColor: 'white', borderColor: '#737373' }}
                >
                  <CloseFullscreenIcon sx={{ position: 'static', stroke: '#737373' }} />
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => window.open(window.location.href, '_blank')}
                  sx={{ textTransform: 'uppercase', backgroundColor: 'white', fontWeight: 'bold', boxShadow: '0 4px 4px 0 rgba(0, 0, 0, 0.25)' }}
                >
                  <img src="/icons/screen-share.svg" alt="" style={{ width: 20, height: 20, marginRight: 4 }} />
                  {t('openPlanningNewTab')}
                </Button>
                
              </Box>
            </Box>
          </Box>

          {/* Header Planning — date, chauffeurs, ODL, recherche */}
          <Box
            sx={{
              display: 'flex',
              padding: '8px 10px',
              alignItems: 'center',
              gap: '24px',
              alignSelf: 'stretch',
              borderRadius: '4px',
              border: '1px solid #C4C4C4',
              background: '#FFF',
              mt: '16px',
              mb: '32px',
              flexShrink: 0,
              flexWrap: 'wrap',
            }}
          >
            <Box sx={{
              padding: 1,
              gap: '6px',
              display: 'flex',
              flexDirection: 'column'
            }}>
              <Typography variant="body1" sx={{ color: '#212529', fontSize: 14, fontWeight: 600 }}>{t('planningDriversTitle')}</Typography>
              <Box display="flex" alignItems="center" gap={0.5} sx={{height: "29px", marginBottom: "10px"}}>
                <Typography variant="body2" sx={{ fontSize: 12 }}>{t('active')}</Typography>
                <Switch
                  checked={driverFilterType === 'all'}
                  onChange={(e) => setDriverFilterType(e.target.checked ? 'all' : 'active')}
                  sx={{
                    padding: 0,
                    width: 43,
                    height: 24,
                    '& .MuiSwitch-switchBase': {
                      padding: 0,
                    },
                    '& .MuiSwitch-thumb': {
                      width: 20,
                      height: 20,
                      marginTop: '2px',
                      backgroundColor: driverFilterType === 'all' ? '#1577FC' : undefined,
                    },
                    '& .MuiSwitch-track': {
                      borderRadius: 12,
                      backgroundColor: driverFilterType === 'all' ? '#1976D233' : undefined,
                    },
                  }}
                />
                <Typography variant="body2" sx={{ fontSize: 12 }}>{t('all')}</Typography>
              </Box>
            </Box>
            <Box sx={{
              padding: 1,
              gap: '6px',
              display: 'flex',
              flexDirection: 'column',
              width: "350px"
            }}>
              <Typography variant="body1" sx={{ color: '#212529', fontSize: 14, fontWeight: 600 }}>{t('odlCount')}</Typography>
              <Slider
                value={odlCountFilter}
                onChange={(_, val) => setOdlCountFilter(val)}
                min={-1}
                max={6}
                step={1}
                marks={[
                  { value: -1, label: t('disabled') },
                  { value: 0, label: '0' },
                  { value: 1, label: '1' },
                  { value: 2, label: '2' },
                  { value: 3, label: '3' },
                  { value: 4, label: '4' },
                  { value: 5, label: '5' },
                  { value: 6, label: '+' },
                ]}
                valueLabelDisplay="off"
                sx={{
                  width: '100%',
                  height: 29,
                  color: odlCountFilter >= 0 ? '#1577FC' : '#B0B0B0',
                  padding: 0,
                  marginBottom: "10px",
                  marginLeft: '6px',
                  '& .MuiSlider-rail': { height: 4, background: odlCountFilter >= 0 ? '#1577FC' : '#D0D0D0', opacity: 1 },
                  '& .MuiSlider-track': { height: 4 },
                  '& .MuiSlider-thumb': {
                    width: 12,
                    height: 12,
                    backgroundColor: odlCountFilter >= 0 ? '#1577FC' : '#B0B0B0',
                  },
                  '& .MuiSlider-mark': { 
                    width: '6px',
                    height: '6px',
                    borderRadius: '3px',
                    transform: 'translate(-3px, -50%)',
                    background: odlCountFilter >= 0 ? '#1577FC' : '#D0D0D0',
                  },
                  '& .MuiSlider-markLabel': { fontSize: 11, color: odlCountFilter >= 0 ? '#1577FC' : '#B0B0B0' },
                }}
              />
            </Box>
            <TextField
              size="small"
              placeholder={t('searchPlanning')}
              value={planningSearchTerm}
              onChange={(e) => setPlanningSearchTerm(e.target.value)}
              InputProps={{ startAdornment: <SearchIcon sx={{ stroke: '#737373', fontSize: 20, mr: 1.5, position: 'static' }} /> }}
               sx={{ flex: 1, minWidth: 100 }}
            />
          </Box>

          {/* Cards Planning — grille 4 colonnes, scroll vertical */}
          <Box sx={{ flex: 1, overflowY: 'auto', pb: 4 }}>
            {loading ? (
              <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                <CircularProgress size={60} />
              </Box>
            ) : (
              <Box
                sx={{
                  columnWidth: 324,
                  columnGap: 2,
                }}
              >
                {filteredDrivers?.map((driver) => (
                  <Box key={driver.id} sx={{ breakInside: 'avoid', mb: 2 }}>
                    <DriverCardFullScreen
                      driver={driver}
                      transporterColor={driver.transporter.color}
                      moveOrder={moveOrder}
                      toggleDriverStatus={toggleDriverStatus}
                      isDragDisabled={isPastDate}
                    />
                  </Box>
                ))}
              </Box>
            )}
          </Box>

          {/* Footer Planning — boutons */}
          <Box
            sx={{
              flexShrink: 0,
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <Button
              variant="contained"
              color="success"
              onClick={onExportExcel}
              sx={{ height: 41 }}
            >
              {t('exportExcel')}
            </Button>
            <Button
              variant="outlined"
              onClick={handleAddOccasionalDriver}
              disabled={isPastDate}
              sx={{ height: 41 }}
            >
              <span style={{ fontSize: '1.2rem', marginRight: '5px' }}>+</span>
              {t('occasionalDriver')}
            </Button>
            <Button
              variant={isPlanificationSentAtLeastOnce ? 'outlined' : 'contained'}
              color={isPlanificationSentAtLeastOnce ? 'success' : 'primary'}
              disabled={!isTodayOrNextBusinessDay && !isPlanificationSentAtLeastOnce}
              onClick={isPlanificationSentAtLeastOnce ? undefined : handleSendPlanification}
              sx={{
                height: 41,
                borderColor: isPlanificationSentAtLeastOnce ? 'green' : undefined,
                color: isPlanificationSentAtLeastOnce ? 'green' : undefined,
                '&:hover': {
                  borderColor: isPlanificationSentAtLeastOnce ? 'darkgreen' : undefined,
                  backgroundColor: isPlanificationSentAtLeastOnce ? 'transparent' : undefined,
                },
              }}
            >
              <span style={{ marginRight: '6px' }}>
                {isPlanificationSentAtLeastOnce ? (
                  <img
                    src="icons/check.png"
                    alt="Check Icon"
                    style={{
                      width: '20px',
                      height: '20px',
                      filter:
                        'invert(36%) sepia(66%) saturate(473%) hue-rotate(90deg) brightness(90%) contrast(85%)',
                    }}
                  />
                ) : (
                  <img
                    src="icons/picto-send.svg"
                    alt="Send Icon"
                    style={{ width: '20px', height: '20px' }}
                  />
                )}
              </span>
              {isPlanificationSentAtLeastOnce ? t('planningSent') : t('sendPlanification')}
            </Button>
          </Box>
        </Box>
        )}
      </Box>

      {/* Modals */}
      <OccasionalDriverModal
        open={isModalOpen}
        onClose={handleModalClose}
        mode="create"
        refresh={handleRefreshPlanning}
        selectedDate={selectedDate}
      />
      <ConfirmationModal
        open={isConfirmationOpen}
        onClose={() => setIsConfirmationOpen(false)}
        onConfirm={handleConfirmSend}
        title={t('confirmSendTitle')}
        message={t('confirmSendMessage')}
      />
      <Dialog open={isAttributionOpen} onClose={() => setIsAttributionOpen(false)} fullWidth maxWidth="sm">
        <CustomDialogTitle>
          <div>
            <h5>{t('attribution')}</h5>
          </div>
          <Button style={{ color: 'inherit', fontSize: '1rem' }} onClick={() => setIsAttributionOpen(false)}>
            X
          </Button>
        </CustomDialogTitle>
        <DialogContent>
          <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>{t('odlSelected')}</Typography>
          {selectedOrderIds.size === 0 ? (
            <Typography variant="body2" sx={{ mb: 2, color: '#737373' }}>{t('noOdlToAttribute')}</Typography>
          ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
            {orders?.orderDeliveries?.edges
              .filter((o) => selectedOrderIds.has(o.node.id))
              .map(({ node }) => (
                  <OrderCardFullScreen
                    key={node.id}
                    deliveryOrder={node}
                    fromDriverId={null}
                    isDragDisabled={true}
                    selected={true}
                    onToggleSelect={handleToggleSelect}
                    isPopup
                  />
              ))}
          </Box>
          )}
          <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>{t('attribution')}</Typography>
          <Box sx={{
            display: 'flex',
            padding: '24px',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: '16px',
            alignSelf: 'stretch',
            borderRadius: '4px',
            border: '1px solid #C4C4C4',
          }}>
            <Typography variant="body2" sx={{ mb: 0.5, color: '#737373' }}>{t('transporterChoice')}</Typography>
            <Autocomplete
              value={drivers?.find(d => d.id === attributionDriverId) || null}
              onChange={(_, newValue) => setAttributionDriverId(newValue?.id || '')}
              options={[...(drivers || [])].sort((a, b) => {
                const nameA = `${a.lastname} ${a.firstname}`.toLowerCase()
                const nameB = `${b.lastname} ${b.firstname}`.toLowerCase()
                return nameA.localeCompare(nameB)
              })}
              getOptionLabel={(option) => `${option.lastname} ${option.firstname}`}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              size="small"
              fullWidth
              sx={{
                '& svg': { position: 'static' },
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('transporterPlaceholder')}
                  placeholder={t('transporterPlaceholder')}
                />
              )}
            />
          </Box>
          {attributionError && (
            <Typography color="error" variant="body2" sx={{ mt: 1 }}>
              {attributionError}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsAttributionOpen(false)} variant="contained" color="warning">
            {t('cancel')}
          </Button>
          <Button variant="contained" color="primary" disabled={!attributionDriverId || selectedOrderIds.size === 0} onClick={handleAttribuer}>
            {t('attribuer')}
          </Button>
        </DialogActions>
      </Dialog>
    </DndProvider>
  )
}

PlanningPageFullScreen.propTypes = {
  selectedDate: PropTypes.any.isRequired,
  setSelectedDate: PropTypes.func,
  weekDays: PropTypes.array,
  drivers: PropTypes.array.isRequired,
  isPlanificationSentAtLeastOnce: PropTypes.bool.isRequired,
  isPastDate: PropTypes.bool.isRequired,
  isTodayOrNextBusinessDay: PropTypes.bool.isRequired,
  loading: PropTypes.bool.isRequired,
  error: PropTypes.any,
  orders: PropTypes.any,

  isModalOpen: PropTypes.bool.isRequired,
  handleModalClose: PropTypes.func.isRequired,
  handleAddOccasionalDriver: PropTypes.func,
  handleRefreshPlanning: PropTypes.func.isRequired,
  handleSendPlanification: PropTypes.func.isRequired,
  handleConfirmSend: PropTypes.func.isRequired,
  setIsConfirmationOpen: PropTypes.func.isRequired,
  isConfirmationOpen: PropTypes.bool.isRequired,
  moveOrder: PropTypes.func.isRequired,
  moveOrderToList: PropTypes.func.isRequired,
  toggleDriverStatus: PropTypes.func.isRequired,
  toggleFullScreen: PropTypes.func.isRequired,
  onExportExcel: PropTypes.func.isRequired,
}

export default PlanningPageFullScreen
